/**
 * PixiReelSpinOverlay — PixiJS-based reel spin animation for the Live Preview.
 *
 * Matches pixi-slot's SpinManager pattern:
 * - Column-based spinning with staggered start/stop
 * - Filler symbols scroll through during spin
 * - Staggered stop with back.out bounce ease
 * - Force-stop snaps instantly to result
 *
 * This overlay sits on top of the PixiPlayGrid during ReelSpinning phase,
 * hides the static grid, runs the animation, then signals completion.
 */
import { Application, Container, Graphics, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import type { CascadeSlotConfig, GridCell, CascadeSymbolConfig } from '@core/types/cascade-slot.types';

export interface ReelSpinConfig {
  /** Delay between column spin starts (seconds). Default 0.08 */
  reelStartDelay: number;
  /** Delay between column stops (seconds). Default 0.12 */
  reelStopDelay: number;
  /** Duration of stop deceleration per column (seconds). Default 0.4 */
  stopDuration: number;
  /** GSAP ease for stop bounce. Default 'back.out(1.7)' */
  stopEase: string;
  /** Minimum total spin time before stopping (seconds). Default 1.0 */
  minimumSpinDuration: number;
  /** Speed multiplier for turbo mode. Default 1 (2 = double speed) */
  turboMultiplier: number;
  /** Number of filler symbols per column during spin. Default 20 */
  fillerCount: number;
  /** Spin speed in pixels/second. Default 2000 */
  spinSpeed: number;
}

const DEFAULT_REEL_SPIN_CONFIG: ReelSpinConfig = {
  reelStartDelay: 0.08,
  reelStopDelay: 0.12,
  stopDuration: 0.4,
  stopEase: 'back.out(1.7)',
  minimumSpinDuration: 1.0,
  turboMultiplier: 1,
  fillerCount: 20,
  spinSpeed: 2000,
};

interface ColumnStrip {
  /** Outer container with mask applied */
  masked: Container;
  /** Inner container that moves (the strip) */
  strip: Container;
  /** Sprites in the strip (filler + result) */
  sprites: Sprite[];
  /** GSAP timeline for this column */
  timeline: gsap.core.Timeline | null;
  /** Whether this column has stopped */
  stopped: boolean;
}

export class PixiReelSpinOverlay {
  readonly container = new Container();

  private columns: ColumnStrip[] = [];
  private config: CascadeSlotConfig | null = null;
  private spinConfig: ReelSpinConfig = { ...DEFAULT_REEL_SPIN_CONFIG };
  private cellWidth = 0;
  private cellHeight = 0;
  private spacing = 4;
  private gridWidth = 0;
  private gridHeight = 0;
  private spinning = false;
  private stopRequested = false;
  private resultGrid: GridCell[][] | null = null;
  private resultTextures: Map<string, Texture> = new Map();
  private onCompleteCallback: (() => void) | null = null;
  private forceStopRequested = false;
  private symbolConfigs: Map<string, CascadeSymbolConfig> = new Map();
  private startTime = 0;

  constructor() {
    this.container.sortableChildren = true;
  }

  /**
   * Initialize the overlay dimensions to match the PixiPlayGrid.
   */
  setup(
    config: CascadeSlotConfig,
    gridWidth: number,
    gridHeight: number,
  ): void {
    this.config = config;
    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.spacing = config.symbolSpacing ?? 4;

    const cols = config.columns;
    const rows = config.rows;
    const availW = gridWidth - this.spacing * (cols - 1);
    const availH = gridHeight - this.spacing * (rows - 1);
    this.cellWidth = Math.max(16, Math.floor(availW / cols));
    this.cellHeight = Math.max(16, Math.floor(availH / rows));

    // Build symbol config lookup
    this.symbolConfigs.clear();
    for (const sym of config.symbols) {
      this.symbolConfigs.set(sym.id, sym);
    }
  }

  /**
   * Start the spin animation.
   * @param currentGrid Current grid state (shown briefly before spinning starts)
   * @param textures Map of symbolId → Texture for rendering
   * @param spinConfig Override spin timing config
   * @param onComplete Called when all columns have stopped
   */
  startSpin(
    currentGrid: GridCell[][],
    textures: Map<string, Texture>,
    spinConfig?: Partial<ReelSpinConfig>,
    onComplete?: () => void,
  ): void {
    if (this.spinning) this.forceStop();

    this.spinConfig = { ...DEFAULT_REEL_SPIN_CONFIG, ...spinConfig };
    this.resultTextures = textures;
    this.onCompleteCallback = onComplete ?? null;
    this.spinning = true;
    this.stopRequested = false;
    this.forceStopRequested = false;
    this.resultGrid = null;
    this.startTime = performance.now();

    this.destroyColumns();
    this.buildColumns(currentGrid, textures);

    // Start each column spinning with staggered delay
    const turbo = this.spinConfig.turboMultiplier;
    for (let c = 0; c < this.columns.length; c++) {
      const delay = (c * this.spinConfig.reelStartDelay) / turbo;
      this.startColumnSpin(c, delay);
    }
  }

  /**
   * Set the result grid. If minimum spin time has elapsed, triggers stop sequence.
   * Otherwise, stop will begin once minimum time elapses.
   */
  setResult(
    resultGrid: GridCell[][],
    textures: Map<string, Texture>,
  ): void {
    this.resultGrid = resultGrid;
    this.resultTextures = textures;

    // Check if minimum spin time has elapsed
    const elapsed = (performance.now() - this.startTime) / 1000;
    const minTime = this.spinConfig.minimumSpinDuration / this.spinConfig.turboMultiplier;

    if (elapsed >= minTime) {
      this.beginStopSequence();
    } else {
      // Schedule stop after remaining time
      const remaining = (minTime - elapsed) * 1000;
      setTimeout(() => {
        if (this.spinning && this.resultGrid && !this.stopRequested) {
          this.beginStopSequence();
        }
      }, remaining);
    }
  }

  /**
   * Force-stop: instantly snap all columns to result positions.
   */
  forceStop(): void {
    if (!this.spinning) return;
    this.forceStopRequested = true;

    for (const col of this.columns) {
      if (col.timeline) {
        col.timeline.kill();
        col.timeline = null;
      }
      col.stopped = true;
    }

    // If we have result grid, snap to it
    if (this.resultGrid) {
      this.snapToResult();
    }

    this.finishSpin();
  }

  /** Clean up all resources */
  destroy(): void {
    this.forceStopRequested = true;
    this.spinning = false;
    this.destroyColumns();
    this.onCompleteCallback = null;
  }

  // ── Private: Column Building ────────────────────────────────────

  private buildColumns(
    currentGrid: GridCell[][],
    textures: Map<string, Texture>,
  ): void {
    if (!this.config) return;
    const cols = this.config.columns;
    const rows = this.config.rows;

    for (let c = 0; c < cols; c++) {
      // Create mask for this column (clips symbols to column bounds)
      const colX = c * (this.cellWidth + this.spacing);
      const colH = rows * (this.cellHeight + this.spacing) - this.spacing;

      const masked = new Container();
      masked.position.set(colX, 0);

      const mask = new Graphics();
      mask.rect(0, 0, this.cellWidth, colH);
      mask.fill({ color: 0xffffff });
      masked.addChild(mask);
      masked.mask = mask;

      // Strip container (moves up/down for scroll effect)
      const strip = new Container();
      masked.addChild(strip);

      const sprites: Sprite[] = [];

      // Place current symbols at rows 0..rows-1
      for (let r = 0; r < rows; r++) {
        const cell = currentGrid[r]?.[c];
        const symbolId = cell?.symbolId || 'H1';
        const texture = textures.get(symbolId) ?? null;
        const sprite = this.createSymbolSprite(symbolId, texture);
        sprite.position.set(0, r * (this.cellHeight + this.spacing));
        strip.addChild(sprite);
        sprites.push(sprite);
      }

      this.container.addChild(masked);
      this.columns.push({ masked, strip, sprites, timeline: null, stopped: false });
    }
  }

  private createSymbolSprite(symbolId: string, texture: Texture | null): Sprite {
    if (texture) {
      const sprite = new Sprite(texture);
      sprite.width = this.cellWidth;
      sprite.height = this.cellHeight;
      return sprite;
    }
    // Fallback: colored rectangle
    const g = new Graphics();
    const sym = this.symbolConfigs.get(symbolId);
    const color = sym?.color ?? 0x444444;
    g.rect(0, 0, this.cellWidth, this.cellHeight);
    g.fill({ color, alpha: 0.6 });
    // Convert Graphics to a texture for Sprite
    // Just use a container approach instead
    const container = new Container();
    container.addChild(g);
    // Return a Sprite with white texture sized appropriately as fallback
    const sprite = new Sprite(Texture.WHITE);
    sprite.width = this.cellWidth;
    sprite.height = this.cellHeight;
    sprite.tint = color;
    sprite.alpha = 0.6;
    return sprite;
  }

  // ── Private: Spin Animation ──────────────────────────────────────

  private startColumnSpin(colIndex: number, delay: number): void {
    const col = this.columns[colIndex];
    if (!col || !this.config) return;

    const rows = this.config.rows;
    const stripHeight = rows * (this.cellHeight + this.spacing);
    const turbo = this.spinConfig.turboMultiplier;
    const speed = this.spinConfig.spinSpeed * turbo;

    // Duration for one full strip cycle
    const cycleDuration = stripHeight / speed;

    const tl = gsap.timeline({ delay });

    // Anticipation: small pull up
    tl.to(col.strip, {
      y: -15,
      duration: 0.12 / turbo,
      ease: 'power2.in',
    });

    // Spin loop: continuously scroll down, repeating
    // We scroll the strip down by stripHeight then reset, creating infinite scroll illusion
    tl.to(col.strip, {
      y: stripHeight,
      duration: cycleDuration,
      ease: 'none',
      repeat: -1, // infinite until killed
      onRepeat: () => {
        // Randomize symbols on each cycle for visual variety
        this.randomizeColumnSymbols(colIndex);
        col.strip.y = 0;
      },
    });

    col.timeline = tl;
  }

  private randomizeColumnSymbols(colIndex: number): void {
    const col = this.columns[colIndex];
    if (!col || !this.config) return;

    const symbols = this.config.symbols;
    for (const sprite of col.sprites) {
      // Pick a random symbol
      const randSym = symbols[Math.floor(Math.random() * symbols.length)];
      const texture = this.resultTextures.get(randSym.id) ?? null;
      if (texture) {
        sprite.texture = texture;
        sprite.tint = 0xffffff;
        sprite.alpha = 1;
      } else {
        sprite.texture = Texture.WHITE;
        sprite.tint = randSym.color ?? 0x444444;
        sprite.alpha = 0.6;
      }
      sprite.width = this.cellWidth;
      sprite.height = this.cellHeight;
    }
  }

  // ── Private: Stop Sequence ───────────────────────────────────────

  private beginStopSequence(): void {
    if (this.stopRequested || !this.resultGrid || !this.config) return;
    this.stopRequested = true;

    const turbo = this.spinConfig.turboMultiplier;
    const cols = this.config.columns;

    for (let c = 0; c < cols; c++) {
      const delay = (c * this.spinConfig.reelStopDelay) / turbo;
      setTimeout(() => {
        if (this.forceStopRequested) return;
        this.stopColumn(c);
      }, delay * 1000);
    }
  }

  private stopColumn(colIndex: number): void {
    const col = this.columns[colIndex];
    if (!col || col.stopped || !this.resultGrid || !this.config) return;

    // Kill the spin timeline
    if (col.timeline) {
      col.timeline.kill();
      col.timeline = null;
    }

    const rows = this.config.rows;
    const turbo = this.spinConfig.turboMultiplier;

    // Set result symbols on the strip
    for (let r = 0; r < rows; r++) {
      const cell = this.resultGrid[r]?.[colIndex];
      if (!cell) continue;
      const sprite = col.sprites[r];
      if (!sprite) continue;

      const texture = this.resultTextures.get(cell.symbolId) ?? null;
      if (texture) {
        sprite.texture = texture;
        sprite.tint = 0xffffff;
        sprite.alpha = 1;
      } else {
        sprite.texture = Texture.WHITE;
        const sym = this.symbolConfigs.get(cell.symbolId);
        sprite.tint = sym?.color ?? 0x444444;
        sprite.alpha = 0.6;
      }
      sprite.width = this.cellWidth;
      sprite.height = this.cellHeight;
    }

    // Animate stop: strip comes from above with bounce
    const overshoot = 20;
    col.strip.y = -(this.cellHeight + this.spacing); // Start from one row above

    const tl = gsap.timeline();
    tl.to(col.strip, {
      y: overshoot, // Overshoot past target
      duration: (this.spinConfig.stopDuration * 0.6) / turbo,
      ease: 'power2.out',
    });
    tl.to(col.strip, {
      y: 0, // Settle at target
      duration: (this.spinConfig.stopDuration * 0.4) / turbo,
      ease: this.spinConfig.stopEase,
      onComplete: () => {
        col.stopped = true;
        this.checkAllStopped();
      },
    });

    col.timeline = tl;
  }

  private snapToResult(): void {
    if (!this.resultGrid || !this.config) return;
    const rows = this.config.rows;

    for (let c = 0; c < this.columns.length; c++) {
      const col = this.columns[c];
      col.strip.y = 0;

      for (let r = 0; r < rows; r++) {
        const cell = this.resultGrid[r]?.[c];
        if (!cell) continue;
        const sprite = col.sprites[r];
        if (!sprite) continue;

        const texture = this.resultTextures.get(cell.symbolId) ?? null;
        if (texture) {
          sprite.texture = texture;
          sprite.tint = 0xffffff;
          sprite.alpha = 1;
        } else {
          sprite.texture = Texture.WHITE;
          const sym = this.symbolConfigs.get(cell.symbolId);
          sprite.tint = sym?.color ?? 0x444444;
          sprite.alpha = 0.6;
        }
        sprite.width = this.cellWidth;
        sprite.height = this.cellHeight;
      }
    }
  }

  private checkAllStopped(): void {
    if (this.columns.every(c => c.stopped)) {
      this.finishSpin();
    }
  }

  private finishSpin(): void {
    if (!this.spinning) return;
    this.spinning = false;

    if (this.onCompleteCallback) {
      this.onCompleteCallback();
      this.onCompleteCallback = null;
    }
  }

  // ── Private: Cleanup ─────────────────────────────────────────────

  private destroyColumns(): void {
    for (const col of this.columns) {
      if (col.timeline) {
        col.timeline.kill();
        col.timeline = null;
      }
      col.masked.destroy({ children: true });
    }
    this.columns = [];
    this.container.removeChildren();
  }
}
